import { formatFullDate, parseLocalDate } from '@/utils/dates'
import { priceLevelLabel } from '@/utils/priceLevel'

export type RecordDetail = { label: string; value: string }

export type RecordSummary = { code?: string | null; name?: string | null; details?: RecordDetail[] }

export type RecordSource<T> = {
  load: (id: string) => Promise<T>
  summarize: (record: T) => RecordSummary
}

const NO_END_DATE_LABEL = 'sin fecha fin'

const activeLabel = (isActive: boolean) => (isActive ? 'Activo' : 'Inactivo')

// Reutiliza priceLevelLabel: level llega null cuando el nivel referenciado ya fue eliminado.
const levelLabel = (level?: { level: number | null; name?: string | null } | null) =>
  level ? priceLevelLabel(level) : null

// Las fechas de vigencia son civiles (sin hora): parseLocalDate evita el desfase de un día por la zona horaria.
const civilDateLabel = (value: string) => formatFullDate(parseLocalDate(value))

const fullName = (...parts: Array<string | null | undefined>) => parts.filter(Boolean).join(' ')

const toDetails = (entries: Array<[string, string | number | null | undefined]>): RecordDetail[] =>
  entries
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([label, value]) => ({ label, value: String(value) }))

// Une el tipo de lo que carga el servicio con el de su resumen, para que cada módulo quede tipado.
const defineSource = <T>(source: RecordSource<T>) => source

// Cada módulo con detalle sabe pedir su registro y resumirlo; los servicios se importan al usarse para no cargarlos todos.
export const RECORD_SOURCES = {
  productos: defineSource({
    load: async id => (await import('@/services/product/catalog')).getProductsById(id),
    summarize: product => ({
      code: product.code,
      name: product.name,
      details: toDetails([
        ['Categoría', product.category?.name],
        ['Subcategoría', product.subcategory?.name],
        ['Marca', product.brand?.name],
        ['Estatus', activeLabel(product.is_active)]
      ])
    })
  }),
  surtidos: defineSource({
    load: async id => (await import('@/services/assortment/catalog')).getAssortmentById(id),
    summarize: assortment => ({
      code: assortment.code,
      name: assortment.name,
      details: toDetails([
        ['Tiendas', assortment.store_count],
        ['Productos', assortment.product_count],
        ['Estatus', activeLabel(assortment.is_active)]
      ])
    })
  }),
  categorias: defineSource({
    load: async id => (await import('@/services/category')).getCategoryById(id),
    summarize: category => ({
      code: category.code,
      name: category.name,
      details: toDetails([
        ['Orden', category.sort_order],
        ['Estatus', activeLabel(category.is_active)]
      ])
    })
  }),
  'grupo-de-precios': defineSource({
    load: async id => (await import('@/services/product/product-price-group')).getProductPriceGroupsById(id),
    summarize: group => ({
      code: group.code,
      name: group.name,
      details: toDetails([
        ['Nivel', levelLabel(group.price_level)],
        ['Surtido', group.assortment?.code],
        ['Moneda', group.currency?.code],
        [
          'Vigencia',
          `${civilDateLabel(group.valid_from)} a ${group.valid_to ? civilDateLabel(group.valid_to) : NO_END_DATE_LABEL}`
        ],
        ['Estatus', activeLabel(group.is_active)]
      ])
    })
  }),
  promociones: defineSource({
    load: async id => (await import('@/services/promotion/catalog')).getPromotionById(Number(id)),
    summarize: ({ promotion }) => ({ code: promotion.code, name: promotion.name })
  }),
  clientes: defineSource({
    load: async id => (await import('@/services/customer/catalog')).getCustomerById(id),
    summarize: customer => ({
      code: customer.code,
      name: customer.name,
      details: toDetails([
        ['Nombre comercial', customer.trade_name],
        ['Tipo', customer.type?.name],
        ['Nivel de precio', levelLabel(customer.price_level)],
        ['Estatus', activeLabel(customer.is_active)]
      ])
    })
  }),
  empleados: defineSource({
    load: async id => (await import('@/services/employee')).getEmployeesById(id),
    summarize: employee => ({
      code: employee.code,
      name: fullName(employee.name, employee.paternal_surname, employee.maternal_surname),
      details: toDetails([
        ['Puesto', employee.job_role?.name],
        ['Empresa', employee.company?.name],
        ['Usuario', employee.username],
        ['Estatus', activeLabel(employee.is_active)]
      ])
    })
  }),
  tiendas: defineSource({
    load: async id => (await import('@/services/store')).getStoresById(id),
    summarize: store => ({
      code: store.code,
      name: store.name,
      details: toDetails([
        ['Número', store.store_number],
        ['Tipo', store.type?.name],
        ['Empresa', store.company?.name],
        ['Estatus', activeLabel(store.is_active)]
      ])
    })
  }),
  'formas-de-pago': defineSource({
    load: async id => (await import('@/services/payment-method')).getPaymentMethodById(id),
    summarize: paymentMethod => ({ code: paymentMethod.code, name: paymentMethod.name })
  }),
  'secuencias-numericas': defineSource({
    load: async id => (await import('@/services/sequence')).getSequenceById(id),
    summarize: sequence => ({ code: sequence.code, name: sequence.name })
  }),
  proveedores: defineSource({
    load: async id => (await import('@/services/digital-services/provider')).getProviderById(id),
    summarize: ({ provider }) => ({ code: provider.code, name: provider.name })
  }),
  usuarios: defineSource({
    load: async id => (await import('@/services/user')).getUsersById(id),
    summarize: user => ({
      name: fullName(user.name, user.surname),
      details: toDetails([
        ['Correo', user.email],
        ['Estatus', activeLabel(user.is_active)]
      ])
    })
  }),
  roles: defineSource({
    load: async id => (await import('@/services/security/role')).getRoleById(id),
    summarize: role => ({ name: role.name, details: toDetails([['Descripción', role.description]]) })
  }),
  puestos: defineSource({
    load: async id => (await import('@/services/job')).getJobsById(id),
    summarize: job => ({ name: job.name })
  })
}

export type RecordSourceKey = keyof typeof RECORD_SOURCES

// Lo que devuelve el servicio de cada módulo: los formularios lo reciben tal cual lo entregaba su servicio.
export type RecordOf<K extends RecordSourceKey> = (typeof RECORD_SOURCES)[K] extends RecordSource<infer T> ? T : never
